import type { TFunction } from 'i18next';
import { translateWithFallback } from './translation';

export const DEFAULT_API_ERROR_KEY = 'errors.generic';

/**
 * Wire code → locale key. Adding a code is one row, never a new branch.
 * Null-prototype: the code arrives from a response body, and a plain literal
 * would answer 'constructor' / 'toString' / '__proto__' off Object.prototype.
 */
export const API_ERROR_MESSAGE_KEYS: Readonly<Record<string, string>> = Object.assign(
  Object.create(null) as Record<string, string>,
  {
    AUTH_INVALID_CREDENTIALS: 'auth.errorCodes.AUTH_INVALID_CREDENTIALS',
    AUTH_MFA_REQUIRED: 'auth.errorCodes.AUTH_MFA_REQUIRED',
    AUTH_INVALID_MFA: 'auth.errorCodes.AUTH_INVALID_MFA',
    AUTH_TENANT_CODE_REQUIRED: 'auth.errorCodes.AUTH_TENANT_CODE_REQUIRED',
    AUTH_DOCUMENT_TENANT_MISMATCH: 'auth.errorCodes.AUTH_DOCUMENT_TENANT_MISMATCH',
    ERR_NETWORK: 'errors.networkError',
    INVALID_REFRESH_RESPONSE: 'errors.serverError',
    ECONNABORTED: 'errors.networkError',
    RATE_LIMIT_EXCEEDED: 'errors.rateLimitExceeded',
    HTTP_429: 'errors.rateLimitExceeded',
    HTTP_401: 'errors.authenticationRequired',
    HTTP_403: 'errors.forbidden',
    CAD_CONVERSION_FAILED: 'cad.thickness.errorCodes.conversionFailed',
    THICKNESS_ANALYSIS_FAILED: 'cad.thickness.errorCodes.analysisFailed',
    // `CADErrorCode` on the backend: persisted on a failed model, run or batch item.
    CAD_ORIGINAL_MISSING: 'cad.thickness.errorCodes.originalMissing',
    CAD_INTERNAL_ERROR: 'cad.thickness.errorCodes.internalError',
  },
);

/** Client-synthesized code families, same table discipline. */
export const API_ERROR_MESSAGE_PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
  [/^HTTP_5\d\d$/, 'errors.serverError'],
];

/**
 * The HTTP status of a rejected request. The axios interceptor normalises every
 * rejection into `{message, code, details}` — there is no `error.response`.
 */
export const apiErrorStatus = (error: unknown): number | undefined => {
  const apiError = error as { code?: string; details?: { http_status?: number } } | null;
  if (typeof apiError?.details?.http_status === 'number') return apiError.details.http_status;
  const match = /^HTTP_(\d{3})$/.exec(apiError?.code ?? '');
  return match ? Number(match[1]) : undefined;
};

/** The structured `detail` a domain refusal carries, preserved by `normalizeApiError`. */
export const apiErrorDetail = (error: unknown): Record<string, unknown> | undefined => {
  const detail = (error as { details?: { detail?: unknown } } | null)?.details?.detail;
  return detail && typeof detail === 'object' && !Array.isArray(detail)
    ? detail as Record<string, unknown>
    : undefined;
};

const codeOf = (error: unknown): string | undefined => {
  const code = (error as { code?: unknown } | null | undefined)?.code;
  return typeof code === 'string' && code ? code : undefined;
};

const namedKey = (code: string | undefined): string | undefined =>
  (code ? API_ERROR_MESSAGE_KEYS[code] : undefined);

const patternKey = (code: string | undefined): string | undefined =>
  (code ? API_ERROR_MESSAGE_PATTERNS.find(([pattern]) => pattern.test(code))?.[1] : undefined);

export const apiErrorMessageKey = (code: string | undefined): string | undefined =>
  namedKey(code) ?? patternKey(code);

/** '' for an absent key, so no rung of the fallback chain can emit a raw key. */
const translated = (t: TFunction, key: string | undefined): string =>
  (key ? translateWithFallback(t, key, '') : '');

/**
 * Server prose is a log artifact, never UI text: an unmapped code resolves to
 * the translated fallback key rather than `error.message`.
 */
export function resolveApiErrorMessage(
  t: TFunction,
  error: unknown,
  opts: { fallbackKey?: string } = {},
): string {
  const code = codeOf(error);
  // A named code says what went wrong, so it outranks the caller's generic label.
  // The HTTP_5xx family does not: "the server erred" is less actionable than which
  // operation failed, so it sits behind the call site's own key.
  return translated(t, namedKey(code))
    || translated(t, opts.fallbackKey)
    || translated(t, patternKey(code))
    || translated(t, DEFAULT_API_ERROR_KEY);
}
